// components/omore/OmoreSessionDetail.tsx
// Full conversation view for a single Omore session

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { fetchSessionDetails } from '../../services/omoreInteractionService';
import { exportSessionDetailsToCSV, downloadCSV } from '../../services/omoreExportService';
import { SeverityColors, FlagSeverityMap } from '../../types/OmoreInteractionTypes';
import type { OmoreSessionDetail as OmoreSessionDetailType } from '../../types/OmoreInteractionTypes';
import './OmoreSessionDetail.css';

const CATEGORY_LABELS: Record<string, string> = {
    self_harm: 'Self-Harm',
    violence: 'Violence',
    sexual: 'Sexual',
    harassment: 'Harassment',
    hate: 'Hate',
    inappropriate: 'Inappropriate',
    prompt_injection: 'Prompt Injection',
    other: 'Other',
};

export const OmoreSessionDetail: React.FC = () => {
    const navigate = useNavigate();
    const { sessionId } = useParams<{ sessionId: string }>();
    const { currentUser, signOut } = useAuth();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [detail, setDetail] = useState<OmoreSessionDetailType | null>(null);


    const loadSession = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const data = await fetchSessionDetails(sessionId!);
            if (!data) {
                setError('Session not found.');
            } else {
                setDetail(data);
            }
        } catch (err: any) {
            console.error('Error loading session:', err);
            setError(err.message || 'Failed to load session');
        } finally {
            setLoading(false);
        }
    }, [sessionId]);

    useEffect(() => {
        if (sessionId) loadSession();
    }, [sessionId, loadSession]);

    const handleSignOut = async () => {
        try {
            await signOut();
            navigate('/login');
        } catch (error) {
            console.error('Failed to sign out:', error);
        }
    };

    const formatTimestamp = (ts: any): string => {
        if (!ts) return 'N/A';
        try {
            const date = ts.toDate ? ts.toDate() : new Date(ts);
            return date.toLocaleString();
        } catch {
            return 'N/A';
        }
    };

    const durationFormatted = detail
        ? `${Math.floor(detail.session.durationSeconds / 60)}m ${detail.session.durationSeconds % 60}s`
        : '0m 0s';

    return (
        <div className="omore-session-container">
            <header className="omore-session-header">
                <div className="header-content">
                    <div>
                        <h1>Session Detail</h1>
                        <p className="header-subtitle">Full conversation transcript</p>
                    </div>
                    <div className="header-actions">
                        <button onClick={() => navigate('/omore/sessions')} className="back-button">← Recent Sessions</button>
                        {detail && (
                            <button
                                onClick={() => {
                                    const csv = exportSessionDetailsToCSV(detail);
                                    downloadCSV(csv, `omore-session-${sessionId}-${Date.now()}.csv`);
                                }}
                                className="export-button"
                            >
                                Export CSV
                            </button>
                        )}
                        <span className="user-email">{currentUser?.email}</span>
                        <button onClick={handleSignOut} className="logout-button">Sign Out</button>
                    </div>
                </div>
            </header>

            <div className="omore-session-main">
                {loading && (
                    <div className="loading-state">
                        <div className="loading-spinner"></div>
                        <p>Loading session...</p>
                    </div>
                )}

                {error && (
                    <div className="error-state">
                        <p>{error}</p>
                        <button onClick={loadSession} className="retry-button">Retry</button>
                    </div>
                )}

                {!loading && !error && detail && (
                    <>
                        {/* Session metadata */}
                        <div className="session-meta-card">
                            <div className="meta-grid">
                                <div className="meta-item">
                                    <span className="meta-label">Explorer</span>
                                    <span className="meta-value">{detail.explorerName}</span>
                                </div>
                                <div className="meta-item">
                                    <span className="meta-label">Helper Type</span>
                                    <span className="meta-value">{detail.session.helperType}</span>
                                </div>
                                <div className="meta-item">
                                    <span className="meta-label">Origin</span>
                                    <span className="meta-value">{detail.session.origin}</span>
                                </div>
                                <div className="meta-item">
                                    <span className="meta-label">Duration</span>
                                    <span className="meta-value">{durationFormatted}</span>
                                </div>
                                <div className="meta-item">
                                    <span className="meta-label">Messages</span>
                                    <span className="meta-value">{detail.session.messageCount}</span>
                                </div>
                                <div className="meta-item">
                                    <span className="meta-label">Tokens Used</span>
                                    <span className="meta-value">{detail.session.totalTokensUsed}</span>
                                </div>
                                <div className="meta-item">
                                    <span className="meta-label">Started</span>
                                    <span className="meta-value">{formatTimestamp(detail.session.startedAt)}</span>
                                </div>
                                <div className="meta-item">
                                    <span className="meta-label">Ended</span>
                                    <span className="meta-value">{formatTimestamp(detail.session.endedAt)}</span>
                                </div>
                                <div className="meta-item">
                                    <span className="meta-label">Suggestions</span>
                                    <span className="meta-value">{detail.session.suggestionsAdopted}/{detail.session.suggestionsShown} adopted</span>
                                </div>
                                <div className="meta-item">
                                    <span className="meta-label">Moderation Flag</span>
                                    <span className={`meta-value ${detail.session.hasModerationFlag ? 'flag-yes' : 'flag-no'}`}>
                                        {detail.session.hasModerationFlag ? 'Yes' : 'No'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Message thread */}
                        <div className="message-thread">
                            {detail.messages.map((msg, idx) => (
                                <div key={msg.id} className={`message-bubble ${msg.role === 'user' ? 'user' : 'omore'}`}>
                                    <div className="bubble-header">
                                        <span className="bubble-role">{msg.role === 'user' ? detail.explorerName : 'Omore'}</span>
                                        <span className="bubble-time">{formatTimestamp(msg.timestamp)}</span>
                                        {msg.isSuggestion && (
                                            <span className={`suggestion-badge ${msg.suggestionAdopted ? 'adopted' : 'not-adopted'}`}>
                                                {msg.suggestionAdopted ? '✓ Adopted' : 'Suggestion'}
                                            </span>
                                        )}
                                    </div>
                                    <div className="bubble-content">{msg.content}</div>

                                    {/* Inline moderation flags */}
                                    {msg.moderationFlags && msg.moderationFlags.length > 0 && (
                                        <div className="bubble-flags">
                                            {msg.moderationFlags.map(cat => (
                                                <span
                                                    key={cat}
                                                    className="flag-tag"
                                                    style={{
                                                        background: (SeverityColors[FlagSeverityMap[cat]] || '#9ca3af') + '20',
                                                        color: SeverityColors[FlagSeverityMap[cat]] || '#9ca3af',
                                                        border: `1px solid ${SeverityColors[FlagSeverityMap[cat]] || '#9ca3af'}`,
                                                    }}
                                                >
                                                    {CATEGORY_LABELS[cat] || cat}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};
